import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTilt } from "../components/useTilt";
import { ArrowUpRight, X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { PORTFOLIO, BRAND } from "../data/landing";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (!u ? u : u.startsWith("http") ? u : `${BACKEND}${u}`);
const resolveItem = (it) => ({
  ...it,
  image: resolveUrl(it.image),
  video: resolveUrl(it.video),
  images: Array.isArray(it.images) ? it.images.map(resolveUrl) : [],
});

const EASE = [0.22, 1, 0.36, 1];

const CARD_REVEAL = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

const CARD_TRANSITIONS = [0, 1, 2].map((m) => ({
  duration: 0.6,
  delay: m * 0.09,
  ease: EASE,
}));

const BACKDROP_MOTION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.25 },
};

const MODAL_MOTION = {
  initial: { opacity: 0, y: 24, scale: 0.97 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 12, scale: 0.98 },
  transition: { duration: 0.35, ease: EASE },
};

// Lightbox open/close/navigation + keyboard handling.
function useLightbox(length) {
  const [active, setActive] = useState(null);

  const open = useCallback((i) => setActive(i), []);
  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(
    () => setActive((i) => (i === null ? null : (i - 1 + length) % length)),
    [length]
  );
  const next = useCallback(
    () => setActive((i) => (i === null ? null : (i + 1) % length)),
    [length]
  );

  useEffect(() => {
    if (active === null) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- effect deps are stable (memoized callbacks + globals); listed items are non-reactive
  }, [active, close, prev, next]);

  return { active, open, close, prev, next };
}

function LogoSlides({ images, pad = "p-10" }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 2200);
    return () => clearInterval(t);
  }, [images.length]);
  return (
    <>
      {images.map((src, i) => (
        <img
          key={i}
          src={src}
          alt=""
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-contain ${pad} transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
        />
      ))}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {images.map((_, i) => (
          <span
            key={i}
            className={`size-1.5 rounded-full transition-colors ${i === idx ? "bg-[#1440E1]" : "bg-[#050B26]/25"}`}
          />
        ))}
      </div>
    </>
  );
}


function ProjectCard({ project, index, onOpen }) {
  const tilt = useTilt(6);
  const isWebsite = project.type === "website" && project.link;
  const handleClick = () => {
    if (isWebsite) {
      window.open(project.link, "_blank", "noopener,noreferrer");
    } else {
      onOpen(index);
    }
  };
  return (
    <motion.article
      layout
      {...CARD_REVEAL}
      exit={{ opacity: 0, scale: 0.92 }}
      transition={CARD_TRANSITIONS[index % 3]}
      whileHover={{ y: -8 }}
      style={tilt.style}
      onMouseMove={tilt.onMouseMove}
      onMouseLeave={tilt.onMouseLeave}
      onClick={handleClick}
      className="project-card group relative overflow-hidden bg-white/[0.04] rounded-[28px] border border-white/8 cursor-pointer transition-shadow duration-300 hover:border-[#1440E1]/45 hover:shadow-[0_30px_70px_-24px_rgba(20,64,225,0.6)]"
      data-testid={`work-card-${index}`}
    >
      <div className={`${project.type === "website" ? "aspect-[16/10] sm:aspect-square" : "aspect-square"} overflow-hidden rounded-t-[28px] relative ${project.type === "logo" ? "bg-white" : "bg-black"}`}>
        {project.type === "video" ? (
          <>
            <video
              src={`${project.video}#t=0.5`}
              poster={project.image}
              muted
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[900ms] ease-out"
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="size-16 rounded-full bg-white/90 text-[#1440E1] flex items-center justify-center shadow-[0_10px_30px_-6px_rgba(0,0,0,0.6)] group-hover:scale-110 transition-transform">
                <Play size={26} className="ml-0.5" fill="currentColor" />
              </span>
            </span>
          </>
        ) : project.type === "logo" ? (
          project.images && project.images.length > 1 ? (
            <LogoSlides images={project.images} pad="p-10 sm:p-12" />
          ) : (
            <img
              src={project.image}
              alt={project.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-contain p-10 sm:p-12 group-hover:scale-[1.06] transition-transform duration-[900ms] ease-out"
            />
          )
        ) : project.type === "website" ? (
          <img
            src={project.image}
            alt={project.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-contain object-center p-3 sm:p-4 group-hover:scale-[1.04] transition-transform duration-[900ms] ease-out"
          />
        ) : (
          <img
            src={project.image}
            alt={project.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.06] transition-transform duration-[900ms] ease-out"
          />
        )}
        {project.type !== "logo" && (
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/35" />
        )}
        <div className={`absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold rounded-full backdrop-blur-md ${project.type === "logo" ? "bg-[#050B26]/90 text-white" : "bg-white/95 text-[#050B26]"}`}>
          {project.type === "video" ? "Watch Video" : project.type === "logo" ? "Brand Logo" : project.type === "website" ? "Visit Site" : "View Project"}
        </div>
      </div>

      <div className="px-6 sm:px-7 py-6 sm:py-7">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#5b80ff] font-semibold mb-3">
          {project.tag}
        </p>
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-display text-lg sm:text-xl font-semibold text-white leading-snug tracking-tight pr-2">
            {project.title}
          </h3>
          <span className="size-10 shrink-0 rounded-full flex items-center justify-center bg-white/8 border border-white/12 text-white/85 group-hover:bg-[#1440E1] group-hover:border-[#1440E1] group-hover:text-white group-hover:rotate-[-12deg] transition-all duration-300">
            <ArrowUpRight size={16} strokeWidth={2.25} />
          </span>
        </div>
      </div>
    </motion.article>
  );
}

function LightboxControls({ onClose, onPrev, onNext }) {
  return (
    <>
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-5 right-5 sm:top-7 sm:right-7 size-11 rounded-full bg-white/10 hover:bg-white hover:text-[#1440E1] text-white border border-white/25 flex items-center justify-center transition-all duration-300 hover:scale-105 backdrop-blur-md shadow-[0_10px_30px_-8px_rgba(0,0,0,0.6)]"
        data-testid="lightbox-close"
      >
        <X size={20} strokeWidth={2.25} />
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        aria-label="Previous project"
        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 size-12 rounded-full bg-white/10 hover:bg-white hover:text-[#1440E1] text-white border border-white/25 flex items-center justify-center transition-all duration-300 hover:scale-105 backdrop-blur-md z-10"
        data-testid="lightbox-prev"
      >
        <ChevronLeft size={22} strokeWidth={2.25} />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        aria-label="Next project"
        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 size-12 rounded-full bg-white/10 hover:bg-white hover:text-[#1440E1] text-white border border-white/25 flex items-center justify-center transition-all duration-300 hover:scale-105 backdrop-blur-md z-10"
        data-testid="lightbox-next"
      >
        <ChevronRight size={22} strokeWidth={2.25} />
      </button>
    </>
  );
}

function LightboxDetail({ project, position, total, onClose }) {
  return (
    <div className="p-7 sm:p-9 flex flex-col justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#5b80ff] font-semibold mb-4">
          {project.tag}
        </p>
        <h3 className="font-arial-bold text-2xl sm:text-3xl text-white leading-tight mb-5">
          {project.title}
        </h3>
        <p className="text-white/65 text-sm sm:text-base leading-relaxed">
          Strategy, creative direction, and production handled in-house at
          Knight Media Group. Want results like this for your brand?
        </p>
        <div className="mt-7 flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-white/45">
          <span>{position} / {total}</span>
          <span className="h-px w-12 bg-white/20" />
          <span>Use ← → keys</span>
        </div>
      </div>
      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <a
          href="#get-started"
          onClick={onClose}
          className="btn-kmg inline-flex items-center justify-center gap-2 bg-[#1440E1] hover:bg-[#1033B3] text-white px-5 py-3 font-bold text-sm tracking-wide"
          data-testid="lightbox-cta-primary"
        >
          Start a project
          <ArrowUpRight size={16} />
        </a>
        <a
          href={BRAND.bookMeetingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-kmg inline-flex items-center justify-center gap-2 border border-white/25 bg-white/10 hover:bg-white/20 text-white px-5 py-3 font-bold text-sm tracking-wide"
          data-testid="lightbox-cta-secondary"
        >
          Book a meeting
        </a>
      </div>
    </div>
  );
}

function Lightbox({ active, project, total, onClose, onPrev, onNext }) {
  return (
    <motion.div
      {...BACKDROP_MOTION}
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 sm:px-8 py-12"
      onClick={onClose}
      data-testid="work-lightbox"
    >
      <div className="absolute inset-0 bg-[#02061A]/92 backdrop-blur-md" />

      <LightboxControls onClose={onClose} onPrev={onPrev} onNext={onNext} />

      <motion.div
        key={active}
        {...MODAL_MOTION}
        className="relative z-[1] w-full max-w-5xl max-h-[88vh] grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-0 bg-[#0A1130] rounded-2xl overflow-hidden border border-white/10 shadow-[0_60px_120px_-30px_rgba(0,0,0,0.9),0_0_0_1px_rgba(91,128,255,0.18)_inset]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`relative aspect-square md:aspect-auto md:min-h-[60vh] flex items-center justify-center ${project.type === "logo" ? "bg-white" : "bg-black"}`}>
          {project.type === "video" ? (
            <video
              src={project.video}
              poster={project.image}
              controls
              autoPlay
              playsInline
              className="w-full h-full max-h-[88vh] object-contain bg-black"
              data-testid="lightbox-video"
            />
          ) : project.type === "logo" ? (
            project.images && project.images.length > 1 ? (
              <LogoSlides images={project.images} pad="p-12 sm:p-16" />
            ) : (
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-contain p-12 sm:p-16"
              />
            )
          ) : (
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-full object-contain md:object-cover"
            />
          )}
        </div>
        <LightboxDetail
          project={project}
          position={active + 1}
          total={total}
          onClose={onClose}
        />
      </motion.div>
    </motion.div>
  );
}

const FILTERS = [
  { id: "all", label: "All Work" },
  { id: "image", label: "Images" },
  { id: "video", label: "Videos" },
  { id: "logo", label: "Logos" },
  { id: "website", label: "Website" },
  { id: "event", label: "Event Photography" },
];

function FilterChip({ label, count, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-colors ${
        active
          ? "bg-[#1440E1] text-white"
          : "bg-white/[0.06] text-white/70 border border-white/12 hover:bg-white/[0.12] hover:text-white"
      }`}
      data-testid={testId}
    >
      {label}
      <span className={`text-xs ${active ? "text-white/80" : "text-white/45"}`}>{count}</span>
    </button>
  );
}

export default function Work() {
  const [allItems, setAllItems] = useState([]);
  const [filter, setFilter] = useState("image");

  useEffect(() => {
    let active = true;
    fetch(`${BACKEND}/api/portfolio`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("bad status"))))
      .then((data) => {
        if (active) setAllItems(data.map(resolveItem));
      })
      .catch(() => {
        if (active) setAllItems(PORTFOLIO); // graceful fallback to bundled data
      });
    return () => {
      active = false;
    };
  }, []);

  const items = useMemo(
    () => (filter === "all" ? allItems : allItems.filter((p) => p.type === filter)),
    [filter, allItems]
  );
  const counts = useMemo(() => {
    const c = {};
    for (const p of allItems) c[p.type] = (c[p.type] || 0) + 1;
    return c;
  }, [allItems]);
  const { active, open, close, prev, next } = useLightbox(items.length);

  const changeFilter = (id) => {
    close();
    setFilter(id);
  };

  const filterCount = (id) => (id === "all" ? allItems.length : counts[id] || 0);

  return (
    <section
      id="work"
      className="relative bg-[#050B26] py-24 sm:py-32 border-t border-white/5"
      data-testid="work-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#1440E1] font-semibold mb-4">
              View Our Work
            </p>
            <h2 className="font-arial-black text-white leading-tight tracking-tight">
              Selected projects.
            </h2>
          </div>
          <p className="text-white/65 max-w-sm leading-relaxed">
            Recent partnerships across beauty, finance, insurance, events and
            more — flyers, campaigns and video reels.
          </p>
        </motion.div>

        <div className="flex flex-wrap gap-3 mb-10" data-testid="work-filters">
          {FILTERS.map((f) => (
            <FilterChip
              key={f.id}
              label={f.label}
              count={filterCount(f.id)}
              active={filter === f.id}
              onClick={() => changeFilter(f.id)}
              testId={`work-filter-${f.id}`}
            />
          ))}
        </div>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7">
          <AnimatePresence mode="popLayout">
            {items.map((p, i) => (
              <ProjectCard key={p.id || p.title} project={p} index={i} onOpen={open} />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {active !== null && items[active] && (
          <Lightbox
            active={active}
            project={items[active]}
            total={items.length}
            onClose={close}
            onPrev={prev}
            onNext={next}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
